import React, { useState, useEffect, useRef, useCallback } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { useAppState } from "../hooks/useAppState";
import { apiService } from "../services/api";
import { isValidTicketNumber, isValidCarPlate } from "../utils/validation";
import { Dialog } from "@headlessui/react";
import { getConfig, getConfigError } from "../services/configManager";
import { Html5Qrcode } from "html5-qrcode";
import PhoneInput from "../components/PhoneInput";


const enum InputKind {
  TICKET = "ticket",
  CAR = "car",
  PHONE = "phone",
}

const getErrMsg = (inputKind: InputKind, kindOfInputs: InputKind[]) => {
  const messages = getConfig().Messages;
  const currentLabel = getLabel(inputKind).toLowerCase();

  const kindDictionary: Record<InputKind, keyof typeof messages> = {
    [InputKind.TICKET]: 'ActiveParkingNotFoundByTicket',
    [InputKind.CAR]: 'ActiveParkingNotFoundByCar',
    [InputKind.PHONE]: 'ActiveParkingNotFoundByPhone',
  };

  const messageKey = kindDictionary[inputKind];
  const customMessage = messages?.[messageKey];

  if (customMessage && customMessage.trim() !== '') {
    return customMessage;
  }

  // Base sentence
  let msg = `We couldn't find an active parking session for this ${currentLabel}.<br>`;

  // Other enabled kinds, excluding the current one
  const alternatives = kindOfInputs.filter(k => k !== inputKind);

  if (alternatives.length > 0) {
    const altLabels = alternatives
      .map(k => getLabel(k).toLowerCase())
      .join(' or ');

    msg += `Try searching using your ${altLabels}, or pay at any payment station or at the exit.`;
  } else {
    msg += 'Please pay at any payment station or at the exit.';
  }

  return msg;
};


const getPlaceholderCombined = (inputKind: string) => {
  switch (inputKind) {
    case InputKind.TICKET:
      return "1234567";
    case InputKind.CAR:
      return "AAA 000";
    default:
      return "";
  }
}

const getLabel = (inputKind: string) => {
  switch (inputKind) {
    case InputKind.TICKET:
      return "Ticket number";
    case InputKind.CAR:
      return "License plate";
    case InputKind.PHONE:
      return "Phone number";
    default:
      return "";
  }
}
const HomePage: React.FC = () => {
  const { setLoading, setError, setTicket, setStep } = useAppState();
  const [inputValue, setInputValue] = useState("");
  const [showCarVerificationModal, setShowCarVerificationModal] =
    useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const qrCodeRef = useRef<Html5Qrcode | null>(null);
  const isStoppingRef = useRef<boolean>(false);
  const initRetryTimeoutRef = useRef<number | null>(null);
  const initRetryCountRef = useRef<number>(0);
  const config = getConfig();
  const [inputKind, setInputKind] = useState<InputKind>(config.FEATURES.KindOfInputs[0] as InputKind);
  const enabled = config.Enabled;

  const memberEnabled = config.FEATURES.Member.Enabled;

  const getUiConfig = () => {
    switch (inputKind) {
      case InputKind.TICKET:
        return {
          placeholderCombined: getPlaceholderCombined(InputKind.TICKET),
          inputType: "tel",
          inputMode: "numeric",
          showCamera: true,
          validation: isValidTicketNumber,
        }
      case InputKind.CAR:
        return {
          placeholderCombined: getPlaceholderCombined(InputKind.CAR),
          inputType: "text",
          inputMode: "text",
          showCamera: false,
          validation: isValidCarPlate,
        }
      case InputKind.PHONE:
        return {
          placeholderCombined: getPlaceholderCombined(InputKind.PHONE),
          inputType: "phone",
          inputMode: "tel",
          showCamera: false,
        }
    }
  }

  const uiConfig = getUiConfig();


  const handleSearch = async (
    value: string = inputValue,
    force: boolean = false,
    kind: InputKind = inputKind
  ) => {
    if (!value.trim()) return;

    setLoading(true);
    try {
      const ticket = await apiService.findTicketByInput(value.trim(), kind, force);
      setTicket(ticket);
      setStep("ticket-summary");
    }
    catch (error) {
      if (config.FEATURES.ForceCreateTicket && kind === InputKind.CAR && !force) {
        setShowCarVerificationModal(true);
      }
      else {
        setError(getErrMsg(kind, config.FEATURES.KindOfInputs as InputKind[]));
        // setError(error instanceof Error ? error.message : "Failed to find ticket");
      }
    }
    finally {
      setLoading(false);
    }
  };

  const isInputValid =
    inputKind === InputKind.PHONE ||
    (inputValue.trim().length > 0 &&
      uiConfig.validation && uiConfig.validation(inputValue));

  const handleCameraClick = () => {
    setShowQRScanner(true);
  };

  const stopScanner = useCallback(async () => {
    if (!qrCodeRef.current || isStoppingRef.current) return;
    isStoppingRef.current = true;
    try {
      await qrCodeRef.current.stop();
      await qrCodeRef.current.clear();
    } catch (error) {
      console.error("Error stopping QR scanner:", error);
    } finally {
      qrCodeRef.current = null;
      isStoppingRef.current = false;
    }
  }, []);

  const handleQRCodeScanned = useCallback(async (decodedText: string) => {
    await stopScanner();

    setShowQRScanner(false);

    // Check if QR contains URL with parameter 'q'
    try {
      const url = new URL(decodedText);
      const qParam = url.searchParams.get("q");
      if (qParam) {
        let ticketValue = qParam.includes(".")
          ? qParam.split(".")[1]
          : qParam;

        if (ticketValue) {
          ticketValue = ticketValue.slice(7, 16);
          setInputValue(ticketValue);
          setInputKind(InputKind.TICKET);
          handleSearch(ticketValue, false, InputKind.TICKET);
          return;
        }
      }
      const tParam = url.searchParams.get("t");
      if (tParam) {
        let ticketValue = tParam.includes(".")
          ? tParam.split(".")[1]
          : tParam;

        if (ticketValue) {
          ticketValue = ticketValue.slice(7, 16);
          setInputValue(ticketValue);
          setInputKind(InputKind.TICKET);
          handleSearch(ticketValue, false, InputKind.TICKET);
          return;
        }
      }
    } catch (e) {
      // Not a URL, continue with original logic
    }

    // Original QR format handling (41 characters)
    if (decodedText.length != 41) {
      setError("Unsupported QR Format");
      return;
    }

    let ticketValue = decodedText.slice(7, 16);
    setInputValue(ticketValue);
    setLoading(true);

    try {
      let ticket = await apiService.findTicketByInput(ticketValue.trim(), InputKind.TICKET, false);
      setTicket(ticket);
      setStep("ticket-summary");
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : "Failed to find ticket");
    }
    finally {
      setLoading(false);
    }
  }, [setLoading, setError, setTicket, setStep, uiConfig, inputKind, config, setInputValue, setInputKind, setShowCarVerificationModal, stopScanner, handleSearch]);

  useEffect(() => {
    const startScannerIfReady = () => {
      if (!showQRScanner || qrCodeRef.current) {
        return;
      }

      const container = document.getElementById("qr-reader");
      if (!container) {
        // Retry a few times while the Dialog content mounts
        if (initRetryCountRef.current < 10) {
          initRetryCountRef.current += 1;
          initRetryTimeoutRef.current = window.setTimeout(startScannerIfReady, 50);
        } else {
          console.error("qr-reader element not found after retries");
          setError("Failed to start scanner. Please try again.");
          setShowQRScanner(false);
        }
        return;
      }

      try {
        const qrCode = new Html5Qrcode("qr-reader");
        qrCodeRef.current = qrCode;

        qrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            (decodedText) => {
              handleQRCodeScanned(decodedText);
            },
            () => { /* ignore scan errors */ }
          )
          .catch((err) => {
            console.error("Error starting QR scanner:", err);
            setError("Failed to access camera. Please check permissions.");
            setShowQRScanner(false);
            qrCodeRef.current = null;
          });
      } catch (e) {
        console.error("Failed to initialize Html5Qrcode:", e);
        setError("Failed to initialize scanner.");
        setShowQRScanner(false);
        qrCodeRef.current = null;
      }
    };

    if (showQRScanner) {
      initRetryCountRef.current = 0;
      startScannerIfReady();
    }

    return () => {
      if (initRetryTimeoutRef.current !== null) {
        window.clearTimeout(initRetryTimeoutRef.current);
        initRetryTimeoutRef.current = null;
      }
      // Ensure we stop scanner on unmount/close
      void stopScanner();
    };
  }, [showQRScanner, handleQRCodeScanned, setError, stopScanner]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ticketParam = urlParams.get("t");

    if (ticketParam) {
      let ticketValue = ticketParam.includes(".")
        ? ticketParam.split(".")[1]
        : ticketParam;

      if (ticketValue) {
        ticketValue = ticketValue.slice(7, 16);
        setInputValue(ticketValue);
        setInputKind(InputKind.TICKET);
        handleSearch(ticketValue, false, InputKind.TICKET);
      }
    }
  }, []);

  if (getConfigError()) {
    return (
      <div className="flex-1 flex items-center justify-center p-5">
        <div className="container-card w-full max-w-sm text-center">
          <h1 className="text-title font-extrabold text-black mb-4">
            Service Unavailable
          </h1>
          <p className="text-md text-text-disabled">
            Service is temporarily unavailable due to configuration issues. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="flex-1 flex items-center justify-center p-5">
        <div className="container-card w-full max-w-sm text-center">
          <h1 className="text-title font-extrabold text-black mb-4">
            Service Unavailable
          </h1>
          <p className="text-md text-text-disabled">
            Service is temporarily unavailable. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center p-5">
      <div className="flex flex-col items-center w-full max-w-sm">
        <div className="container-card w-full">
        <h1 className="text-title font-extrabold text-black text-center mb-8">
          Enter Details to Begin
        </h1>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 rounded-[10px] bg-[#FAFAFA] border border-[#E9EAEB] p-2">
              {config.FEATURES.KindOfInputs.map(kind => (
                <button
                  key={kind}
                  className={`w-full font-bold capitalize py-2 rounded-md ${inputKind === kind
                    ? "bg-[#0985EB0D] text-[#0985EB]"
                    : "bg-transparent text-[#717680]"
                    }`}
                  onClick={() => {
                    setInputKind(kind as InputKind);
                    setInputValue("");
                  }}

                >
                  {getLabel(kind)}
                </button>
              ))}
            </div>


            <div className="relative">
              {inputKind === InputKind.PHONE ?
                <PhoneInput
                  value={inputValue}
                  onChange={(phone) => setInputValue(phone)}
                />
                : (
                  <>
                    <input
                      type={uiConfig.inputType}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={uiConfig.placeholderCombined}
                      className="input-field w-full pr-10"
                      maxLength={inputKind === InputKind.CAR ? 10 : 60}
                      inputMode={uiConfig.inputMode as "numeric" | "text"}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                    {uiConfig.showCamera && <button
                      onClick={handleCameraClick}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
                      type="button"
                    >
                      <CameraIcon className="h-6 w-6" />
                    </button>
                    }
                  </>
                )}
            </div>
          </div>

          <button
            onClick={() => handleSearch()}
            disabled={!isInputValid}
            className="btn-primary w-full"
          >
            search
          </button>
        </div>
        </div>
        {memberEnabled && (
          <div className="text-center mt-8">
            <a
              href={config.API.LOGIN_URL}
              className="text-[#045CB3] underline decoration-[#045CB3] hover:opacity-80"
            >
              Sign in
            </a>
          </div>
        )}
      </div>

      {showCarVerificationModal && (
        <Dialog
          open={true}
          onClose={() => setShowCarVerificationModal(false)}
          className="relative z-50"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-xl p-6 max-w-sm w-full">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <svg
                    width="77"
                    height="75"
                    viewBox="0 0 77 75"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="46.25"
                      width="71"
                      height="18"
                      rx="5"
                      stroke="#BA0303"
                      stroke-width="4.56"
                    />
                    <path
                      d="M17.9326 52.25C19.8427 52.2502 21.3816 53.7889 21.3818 55.6758C21.3818 57.5628 19.8428 59.1024 17.9326 59.1025C16.0223 59.1025 14.4824 57.5629 14.4824 55.6758C14.4826 53.7888 16.0224 52.25 17.9326 52.25Z"
                      stroke="#BA0303"
                      stroke-width="2"
                    />
                    <path
                      d="M59.0693 52.25C60.9794 52.2502 62.5184 53.7889 62.5186 55.6758C62.5186 57.5628 60.9795 59.1024 59.0693 59.1025C57.159 59.1025 55.6191 57.5629 55.6191 55.6758C55.6193 53.7888 57.1591 52.25 59.0693 52.25Z"
                      stroke="#BA0303"
                      stroke-width="2"
                    />
                    <path
                      d="M9 64.25H19V69.2992C19 70.9289 17.6789 72.25 16.0492 72.25H11.9508C10.3211 72.25 9 70.9289 9 69.2992V64.25Z"
                      stroke="#BA0303"
                      stroke-width="3"
                    />
                    <path
                      d="M58 64.25H68V69.2992C68 70.9289 66.6789 72.25 65.0492 72.25H60.9508C59.3211 72.25 58 70.9289 58 69.2992V64.25Z"
                      stroke="#BA0303"
                      stroke-width="3"
                    />
                    <path
                      d="M27.6434 31.4373C27.6434 31.4373 21.3147 31.2406 18.3481 31.7324C13.6016 34.6831 10.1406 45.896 10.1406 45.896"
                      stroke="#BA0303"
                      stroke-width="5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M49.257 31.4373C49.257 31.4373 55.5857 31.2406 58.5522 31.7324C63.2988 34.6831 66.7598 45.896 66.7598 45.896"
                      stroke="#BA0303"
                      stroke-width="5"
                      stroke-linecap="round"
                    />
                    <g clip-path="url(#clip0_654_5059)">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M29.8469 12.8184C29.8469 13.8857 28.976 14.752 27.9029 14.752C26.8299 14.752 25.959 13.8857 25.959 12.8184C25.959 5.88159 31.6208 0.25 38.5947 0.25C45.5687 0.25 51.2305 5.88159 51.2305 12.8184C51.2305 18.2005 47.8227 22.7967 43.027 24.5901C41.57 25.1624 40.6106 26.5623 40.6106 28.1218C40.6106 28.1498 40.6096 28.1779 40.6087 28.2059C40.5387 29.7934 40.5387 31.1875 40.5387 31.1875C40.5387 32.2548 39.6678 33.1211 38.5947 33.1211C37.5217 33.1211 36.6508 32.2548 36.6508 31.1875C36.6508 31.1875 36.6508 29.7383 36.7227 28.0947C36.7334 24.9449 38.6822 22.1218 41.6351 20.9791C41.6429 20.9762 41.6506 20.9733 41.6574 20.9714C44.9767 19.7348 47.3425 16.5492 47.3425 12.8184C47.3425 8.01628 43.4225 4.11719 38.5947 4.11719C33.7669 4.11719 29.8469 8.01628 29.8469 12.8184Z"
                        fill="#BA0303"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M38.5951 35.8252C40.0406 35.8252 41.2137 36.9921 41.2137 38.4298C41.2137 39.8676 40.0406 41.0344 38.5951 41.0344C37.1497 41.0344 35.9766 39.8676 35.9766 38.4298C35.9766 36.9921 37.1497 35.8252 38.5951 35.8252Z"
                        fill="#BA0303"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_654_5059">
                        <rect
                          width="27"
                          height="41"
                          fill="white"
                          transform="translate(25 0.25)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                <Dialog.Title className="text-black font-bold text-xl mb-2">
                  We could not locate the plate {inputValue}
                </Dialog.Title>

                <Dialog.Description className="text-gray-600 text-md">
                  Are you sure this is your vehicle?
                </Dialog.Description>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowCarVerificationModal(false);
                    handleSearch(inputValue, true);
                  }}
                  className="btn-primary w-full"
                >
                  Yes, that is my plate
                </button>

                <button
                  onClick={() => setShowCarVerificationModal(false)}
                  className="btn-secondary w-full"
                >
                  No, edit plate
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {showQRScanner && (
        <Dialog
          open={true}
          onClose={async () => {
            await stopScanner();
            setShowQRScanner(false);
          }}
          className="relative z-50"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-xl p-6 max-w-sm w-full">
              <Dialog.Title className="text-black font-bold text-xl mb-4 text-center">
                Scan QR Code
              </Dialog.Title>

              <div className="mb-4">
                <div id="qr-reader" className="w-full rounded-lg overflow-hidden"></div>
              </div>

              <button
                onClick={async () => {
                  await stopScanner();
                  setShowQRScanner(false);
                }}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default HomePage;
